import { sparkToast } from '@spark';
import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';

/** Fired on ``window`` when the backend refuses new work because a platform update is under way (HTTP 423). */
export const PLATFORM_LOCKED_EVENT = 'platform-upgrader:locked';

function announceIfLocked(status) {
  if (status === 423) window.dispatchEvent(new Event(PLATFORM_LOCKED_EVENT));
}

export function get_base_url(url) {
  const backendUrl = import.meta.env.VITE_API_LAYER_URL
  if (backendUrl) {
    return backendUrl;
  }

  const portRegex = /:(3000|4000|8760)$/;
  const match = url.match(portRegex);


  if (match) {
    const result = url.slice(0, match.index) + ":8000"; // Slice the URL up to the port
    return result;
  } else {
    const result = url + ":8000";
    return result;
  }
}

export async function data_from_server_get(suffix, callback_func) {
  const url = `${get_base_url(origin)}/${suffix}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    console.log(`Error: ${response.status} ${response.statusText}`);
  }

  const responseJson = await response.json();
  callback_func(responseJson);  // Return the response data (if successful)
}

export async function data_from_server_post(suffix, data, success_callback_func, failure_callback_func) {
  // Collapse identical concurrent requests to avoid duplicate network calls.
  // Keyed by URL + request body. This keeps behavior compatible while reducing overhead.
  if (!window.__optune_inflight_posts__) {
    window.__optune_inflight_posts__ = new Map();
  }
  const inflight = window.__optune_inflight_posts__;

  const url = `${get_base_url(origin)}/${suffix}`;
  const dataToSend = JSON.stringify(data);  // Convert data to JSON format
  const inflightKey = JSON.stringify({ url, body: dataToSend });

  // If the exact same request is already in-flight, piggyback on it.
  if (inflight.has(inflightKey)) {
    try {
      const responseJson = await inflight.get(inflightKey);
      if (success_callback_func) {
        success_callback_func(responseJson);
      }
      return responseJson;
    } catch (e) {
      if (failure_callback_func) {
        // Second argument: the raw error, which carries `status` so callers can
        // branch on it (e.g. 409 → inline field error instead of a toast).
        failure_callback_func(e?.detail || e?.message || e, e);
      } else {
        sparkToast.error(e?.detail || e?.message || e);
      }
      return;
    }
  }

  const requestPromise = (async () => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: dataToSend,
      credentials: 'include',
    });

    if (!response.ok) {
      console.log(`Error: ${response.status} ${response.statusText}`);
      announceIfLocked(response.status);
      let errorDetail = '';
      try {
        const errorData = await response.json();
        errorDetail = formatApiErrorDetail(errorData?.detail || errorData?.message || '');
      } catch (parseError) {
        try {
          errorDetail = await response.text();
        } catch (textError) {
          errorDetail = 'Unknown error occurred';
        }
      }
      const err = new Error(errorDetail || `Request failed: ${response.status}`);
      err.detail = errorDetail;
      err.status = response.status;
      console.error('Error:', err);
      throw err;
    }

    // Defensive: refuse to buffer non-JSON bodies into memory. A stale backend that
    // streams a multi-GB binary (e.g. model tar/zip) as the POST response would
    // otherwise crash the renderer when `response.json()` tries to load it all
    // at once. Cancel the stream and surface a structured error to the caller.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.toLowerCase().includes('application/json')) {
      try {
        response.body?.cancel?.();
      } catch (_cancelErr) {
        // ignore; the body is best-effort cancellable
      }
      const err = new Error(
        `Unexpected non-JSON response (Content-Type: ${contentType || 'unknown'}).`
      );
      err.detail = err.message;
      err.contentType = contentType;
      throw err;
    }

    return await response.json();
  })();

  inflight.set(inflightKey, requestPromise);
  let responseJson;
  try {
    responseJson = await requestPromise;
  } catch (e) {
    if (failure_callback_func) {
      // See the piggyback branch above: `e` is passed so callers can read `status`.
      failure_callback_func(e?.detail || e?.message || e, e);
    } else {
      console.error(`Request failed: ${e?.detail || e?.message || e}`);
    }
    return;
  } finally {
    inflight.delete(inflightKey);
  }

  if (success_callback_func) {
    success_callback_func(responseJson);  // Return the response data (if successful)
  }
  return responseJson;
}

/**
 * POST multipart/form-data with upload progress.
 * Uses XMLHttpRequest because fetch doesn't expose upload progress events.
 *
 * @param {string} url
 * @param {FormData} formData
 * @param {Object} options
 * @param {(progress: {loaded: number, total: number, percent: number|null}) => void} options.onUploadProgress
 * @param {boolean} options.withCredentials
 * @returns {Promise<{ ok: boolean, status: number, data: any, rawText: string }>}
 */
export function post_formdata_with_progress(url, formData, options = {}) {
  const { onUploadProgress, withCredentials = true, onAbortReady } = options || {};

  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = !!withCredentials;

      if (typeof onAbortReady === 'function') {
        onAbortReady(() => xhr.abort());
      }

      if (xhr.upload && typeof onUploadProgress === 'function') {
        xhr.upload.onprogress = (e) => {
          const loaded = e?.loaded ?? 0;
          const total = e?.total ?? 0;
          const percent = e?.lengthComputable && total > 0 ? (loaded / total) * 100 : null;
          onUploadProgress({ loaded, total, percent });
        };
      }

      xhr.onload = () => {
        const status = xhr.status || 0;
        announceIfLocked(status);
        const rawText = xhr.responseText || '';
        let data = null;
        try {
          data = rawText ? JSON.parse(rawText) : null;
        } catch {
          data = rawText;
        }
        resolve({ ok: status >= 200 && status < 300, status, data, rawText });
      };

      xhr.onerror = () => {
        reject(new Error('Network error occurred during upload.'));
      };

      xhr.onabort = () => {
        resolve({ ok: false, status: 0, data: null, rawText: '', aborted: true });
      };

      xhr.send(formData);
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * HTTP PUT with upload progress (e.g. signed GCS/S3 URL).
 * Defaults to withCredentials=false so cross-origin uploads work with
 * Access-Control-Allow-Origin: * (browsers forbid * when credentials are included).
 * @param {string} url
 * @param {Blob|File} body
 * @param {string} contentType
 * @param {Object} [options]
 * @param {boolean} [options.withCredentials=false]
 * @returns {Promise<{ ok: boolean, status: number, data: any, rawText: string, aborted?: boolean }>}
 */
export function put_blob_with_progress(url, body, contentType, options = {}) {
  const { onUploadProgress, withCredentials = false, onAbortReady } = options || {};

  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      xhr.withCredentials = !!withCredentials;
      if (contentType) {
        xhr.setRequestHeader('Content-Type', contentType);
      }

      if (new URL(url).host.endsWith('.blob.core.windows.net')) {
        // Azure signed URL must behave differently
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      }

      if (typeof onAbortReady === 'function') {
        onAbortReady(() => xhr.abort());
      }

      if (xhr.upload && typeof onUploadProgress === 'function') {
        xhr.upload.onprogress = (e) => {
          const loaded = e?.loaded ?? 0;
          const total = e?.total ?? 0;
          const percent = e?.lengthComputable && total > 0 ? (loaded / total) * 100 : null;
          onUploadProgress({ loaded, total, percent });
        };
      }

      xhr.onload = () => {
        const status = xhr.status || 0;
        const rawText = xhr.responseText || '';
        let data = null;
        try {
          data = rawText ? JSON.parse(rawText) : null;
        } catch {
          data = rawText;
        }
        resolve({ ok: status >= 200 && status < 300, status, data, rawText });
      };

      xhr.onerror = () => {
        reject(new Error('Network error occurred during upload.'));
      };

      xhr.onabort = () => {
        resolve({ ok: false, status: 0, data: null, rawText: '', aborted: true });
      };

      xhr.send(body);
    } catch (e) {
      reject(e);
    }
  });
}

export async function download_file_from_server(suffix, data, filename) {
  const url = `${get_base_url(origin)}/${suffix}`;
  const dataToSend = JSON.stringify(data);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: dataToSend,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check if the response is a file download
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/zip')) {
      // Handle file download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      return { success: true, message: "File downloaded successfully!" };
    } else {
      // Handle JSON response (error case)
      const responseJson = await response.json();
      return responseJson;
    }
  } catch (error) {
    console.error('Download error:', error);
    return { success: false, message: error.message };
  }
}

export async function download_file_with_progress(suffix, data, filename, onProgress) {
  const url = `${get_base_url(origin)}/${suffix}`;
  const dataToSend = JSON.stringify(data);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: dataToSend,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check if the response is a file download
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/zip')) {
      // Get content length for progress calculation
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      // Handle file download with progress tracking
      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        // Calculate progress (30% to 100%)
        if (total > 0 && onProgress) {
          const progress = 30 + (receivedLength / total) * 70;
          onProgress(Math.round(progress));
        }
      }

      // Combine chunks into a blob
      const blob = new Blob(chunks, { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // Call progress callback with 100% when complete
      if (onProgress) {
        onProgress(100);
      }

      return { success: true, message: "File downloaded successfully!" };
    } else {
      // Handle JSON response (error case)
      const responseJson = await response.json();
      return responseJson;
    }
  } catch (error) {
    console.error('Download error:', error);
    return { success: false, message: error.message };
  }
}

export async function data_from_server_delete(suffix, data, success_callback_func, failure_callback_func) {
  const url = `${get_base_url(origin)}/${suffix}`;
  const dataToSend = JSON.stringify(data);  // Convert data to JSON format
  const error_details = ""
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: dataToSend,
    credentials: 'include',
  }).catch(e => {
    if (failure_callback_func) {
      failure_callback_func(e.detail)
    } else {
      sparkToast.error(e.detail)
    }
  });

  if (!response) {
    return;
  }

  if (!response.ok) {
    console.log(`Error: ${response.status} ${response.statusText}`);
  }

  const responseJson = await response.json();
  if (success_callback_func) {
    success_callback_func(responseJson);  // Return the response data (if successful)
  }
}
